import { PromotionPlacementZod, type PromotionType } from '@onr/schema'
import { z } from 'zod'

export const createPromotionFormZod = (promotionType?: PromotionType) =>
  z
    .object({
      partnerName: z.string().min(1, { message: 'field.required' }).optional().default(''),
      title: z.string().min(1, { message: 'field.required' }).optional().default(''),
      targetUrl: z
        .string()
        .default('')
        .superRefine((val, ctx) => {
          // Required for local and national promotions
          if (promotionType && promotionType !== 'fallback' && !val) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'field.required',
            })
            return // Stop validation here if required and empty
          }

          // Must be valid URL if provided (only validate format if not empty)
          if (val && !z.string().url().safeParse(val).success) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'field.invalid.url',
            })
          }
        }),
      active: z.boolean().optional().default(true),
      placements: z.record(PromotionPlacementZod, z.boolean().optional().default(false)),
      creativeUrls: z
        .record(
          PromotionPlacementZod,
          z.union([z.string().url({ message: 'field.invalid.url' }), z.string().length(0), z.undefined()])
        )
        .optional()
        .default({}),
    })
    .superRefine((data, ctx) => {
      // When active, each checked placement must have a creative URL
      if (!data.active) return

      for (const [placement, isChecked] of Object.entries(data.placements)) {
        const url = data.creativeUrls[placement as keyof typeof data.creativeUrls]
        if (isChecked && (!url || url === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'field.image.required',
            path: ['creativeUrls', placement],
          })
        }
      }
    })

export type PromotionFormData = z.infer<ReturnType<typeof createPromotionFormZod>>
